import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PaymentMethodForm from '../../views/settings/billing/billing-overview/PaymentMethodForm';
import { fetchData } from 'services/api/fetchData';
import { encryptData } from 'services/authentication/encryptData';
import { getTrialRemainingDays } from 'services/utils/dateTimeUtils';

const TopBar: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { initializeModal } = useModal();
    const { setIsTopNavbarEnabled } = useGlobalContext();
    const { environmentType, subscriptionEndDate, subscriptionNoSepaMandate, setSubscriptionNoSepaMandate, subscriptionIsProvisional, handleLogout, environmentHash } = useAuthContext();

    // Determine if the topbar should be enabled
    useEffect(() => {
        // Show warning topbar in production admin environment
        if (environmentType === 'admin' && process.env.NODE_ENV === 'production') {
            setIsTopNavbarEnabled(true);
        }

        // Show trial topbar
        else if (environmentType === 'trial' && subscriptionEndDate) {
            setIsTopNavbarEnabled(true);
        }
        
        // Show topbar if no payment method is added for client environments
        else if (environmentType === 'client' && (subscriptionNoSepaMandate || subscriptionIsProvisional)) {
            setIsTopNavbarEnabled(true);
        }

        // By default, don't show the topbar
        else {
            setIsTopNavbarEnabled(false);
        }
    }, [environmentType, subscriptionEndDate, subscriptionNoSepaMandate, ,subscriptionIsProvisional, setIsTopNavbarEnabled]);

    // Handle opening of the payment method modal
    const handleOpenPaymentMethodModal = async () => {

        // Fetch the main company details
        const response = await fetchData({ 
            apiUrl: `get_main_company_details/${environmentHash}`,
            handleLogout
        });

        if (response) {
            initializeModal(
                <PaymentMethodForm billingData={response} />, { 
                    title: t('billing.general.add_payment_method'),
                    modalSize: 'small-medium', 
                    onSuccess: () => {
                        // Update the authentication context to hide the no payment method banner
                        setSubscriptionNoSepaMandate(false);
                        localStorage.setItem("no_payment_method", encryptData(String(false)));
                    },
                }
            )
        }        
    }

    // Determine to render the topbar
    const renderTopBar = () => {

        // Show warning topbar in production admin environment
        if (environmentType === 'admin' && process.env.NODE_ENV === 'production') {
            return (
                <div className='topbar warning'>
                    {t('nav.environment_type_topbar_label', { environmentType: environmentType.charAt(0).toUpperCase() + environmentType.slice(1) })}
                </div>
            )
        }

        // Show trial topbar
        else if (environmentType === 'trial' && subscriptionEndDate) {
            return (
                <div className='topbar subtile'>  
                    <span className='link'
                          onClick={() => history.push(`/settings/billing/subscription/wizard/1`)}>
                        {t('nav.remaining_trial_days_topbar_label', { remainingDays: getTrialRemainingDays(subscriptionEndDate) })}
                    </span>
                </div>
            )           
        }

        // Handle client topbars
        else if (environmentType === 'client') {

            // For provisional subscriptions, show banner to redirect to billing page
            if (subscriptionIsProvisional) {
                return (
                    <div className='topbar warning'>
                        <span
                            className='link'
                            onClick={() => history.push(`/settings/billing/subscription`)}
                        >
                            {t('nav.add_payment_details_topbar_label')}
                        </span>
                    </div>
                );
            }

            // If no active sepa payment method exists, show banner to open payment method modal
            if (subscriptionNoSepaMandate) {
                return (
                    <div className='topbar danger'>
                        <span className='link'
                              onClick={handleOpenPaymentMethodModal}>
                            {t('nav.no_payment_method_topbar_label')}
                        </span>
                    </div>
                )
            }
        }

        return null;
    };

    return (
        renderTopBar()
    )
};

export default TopBar;